import React, { useState, useCallback, useEffect, useRef } from 'react';
import { X, MapPin, Heart, MessageCircle, ChevronLeft, ChevronRight, Share2, BedDouble, Bath, Home, Eye } from 'lucide-react';
import api from '../../api/axiosConfig'; 
import { getCSRFToken } from '../../utils/auth';
import { useTranslation } from 'react-i18next';
import { stringToColor } from '../../utils/avatarColor';
import CommentPanel from '../../Components/PropertyCard/CommentPanel';
import { useNavigate } from 'react-router-dom';
import RelatedProperties from './RelatedProperties';
import { FeatureDetector } from './FeatureDetector';

const MIN_HORIZONTAL_SWIPE = 50;
const MIN_VERTICAL_SWIPE = 60;

export default function PropertyModal({ 
  property: initialProperty, 
  properties = [], 
  initialIndex = 0,
  isOpen, 
  onClose, 
  onMessageOwner 
}) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const [currentPropertyIndex, setCurrentPropertyIndex] = useState(
    Array.isArray(properties) && properties.length > 0 && initialIndex >= 0
      ? initialIndex
      : 0
  );
  const currentProperty = properties[currentPropertyIndex] || initialProperty;

  const [liked, setLiked] = useState(currentProperty?.is_liked || false); 
  const [likesCount, setLikesCount] = useState(currentProperty?.likes_count || 0);
  const [commentsCount, setCommentsCount] = useState(currentProperty?.comments_count || 0);
  const [isFollowing, setIsFollowing] = useState(false);

  const [startX, setStartX] = useState(null);
  const [startY, setStartY] = useState(null);
  const [endX, setEndX] = useState(null);
  const [endY, setEndY] = useState(null);

  const [dragStartX, setDragStartX] = useState(null);
  const [dragStartY, setDragStartY] = useState(null);
  const [dragEndX, setDragEndX] = useState(null);
  const [dragEndY, setDragEndY] = useState(null);

  const { t } = useTranslation('PropertyModal');
  const [commentPanel, setCommentPanel] = useState({ isOpen: false, comments: []});
  const navigate = useNavigate();

  const [userCurrency, setUserCurrency] = useState('RWF');
  const [fxRates, setFxRates] = useState({}); 

  const [featureDetector, setFeatureDetector] = useState(null);
  const imageContainerRef = useRef(null);

  // Follow toggle
  const toggleFollow = async () => {
    if (!currentProperty?.owner?.id) return;
    try {
      if (isFollowing) {
        await api.post(`/users/${currentProperty.owner.id}/unfollow/`); 
        setIsFollowing(false);
      } else {
        await api.post(`/users/${currentProperty.owner.id}/follow/`);    
        setIsFollowing(true);
      }
    } catch (err) {
      console.error('Follow failed:', err);
      alert(t('propertyModal.errors.followFailed'));
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    if (Array.isArray(properties) && properties.length > 0) {
      if (Number.isInteger(initialIndex) && initialIndex >= 0 && initialIndex < properties.length) {
        setCurrentPropertyIndex(initialIndex);
        return;
      }

      const foundIdx = initialProperty?.id
        ? properties.findIndex(p => String(p.id) === String(initialProperty.id))
        : -1;
      if (foundIdx >= 0) {
        setCurrentPropertyIndex(foundIdx);
        return;
      }
      setCurrentPropertyIndex(0);
      return;
    }

    setCurrentPropertyIndex(0);
  }, [isOpen, initialIndex, initialProperty, properties]);

  // Fetch follow status
  useEffect(() => {
    if (isOpen && currentProperty?.owner?.id) {
      const fetchFollowStatus = async () => {
        try {
          const res = await api.get(`/users/${currentProperty.owner.id}/follow_status/`);
          setIsFollowing(res.data.is_following || false);
        } catch (err) {
          console.warn('Could not fetch follow status');
        }
      };
      fetchFollowStatus();
    }
  }, [isOpen, currentProperty?.owner?.id]);

  useEffect(() => {
    const loadCurrencyAndFx = async () => {
      const savedCurrency = localStorage.getItem('preferredCurrency') || 'RWF';
      setUserCurrency(savedCurrency);
      try {
        const fxRes = await api.get('/fx-rates/');
        setFxRates(fxRes.data);
      } catch (err) {
        console.error('Failed to fetch FX rates in PropertyModal:', err);
      }
    };
    loadCurrencyAndFx();
  }, []);

  // Sync modal state on open AND keep in sync when parent props change
  useEffect(() => {
    const prop = properties[currentPropertyIndex] || initialProperty;
    
    if (!prop) {
      document.body.style.overflow = 'unset';
      return;
    }

    if (isOpen) {
      setCurrentImageIndex(0);
    }

    setLiked(Boolean(prop.is_liked));
    setLikesCount(Number(prop.likes_count ?? 0));
    setCommentsCount(Number(prop.comments_count ?? 0));

    document.body.style.overflow = isOpen ? 'hidden' : 'unset';

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, initialProperty, properties, currentPropertyIndex]);


  const formatPrice = (priceRwf) => {
    if (!fxRates || !userCurrency || userCurrency === 'RWF') {
      const num = Number(priceRwf);
      if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
      if (num >= 1_000) return `${(num / 1_000).toFixed(0)}K`;
      return num.toString();
    }

    const rwfRate = fxRates['RWF'];
    const targetRate = fxRates[userCurrency];

    if (!rwfRate || !targetRate) {
      // Fallback to RWF formatting
      const num = Number(priceRwf);
      if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
      if (num >= 1_000) return `${(num / 1_000).toFixed(0)}K`;
      return num.toString();
    }

    const priceInUsd = priceRwf / rwfRate;
    const priceInTarget = priceInUsd * targetRate;
    const rounded = Math.round(priceInTarget);

    if (rounded >= 1_000_000) return `${(rounded / 1_000_000).toFixed(1)}M`;
    if (rounded >= 1_000) return `${(rounded / 1_000).toFixed(0)}K`;
    return rounded.toString();
  };

  // Image navigation
  const nextImage = useCallback(() => {
    if (!currentProperty?.images || currentProperty.images.length <= 1) return;
    setCurrentImageIndex(prev => 
      prev === currentProperty.images.length - 1 ? 0 : prev + 1
    );
  }, [currentProperty?.images]);

  const prevImage = useCallback(() => {
    if (!currentProperty?.images || currentProperty.images.length <= 1) return;
    setCurrentImageIndex(prev => 
      prev === 0 ? currentProperty.images.length - 1 : prev - 1
    );
  }, [currentProperty?.images]);

  // Property navigation
  const goToNextProperty = useCallback(() => {
    if (currentPropertyIndex < properties.length - 1) {
      setCurrentPropertyIndex(prev => prev + 1);
    }
  }, [currentPropertyIndex, properties.length]);

  const goToPrevProperty = useCallback(() => {
    if (currentPropertyIndex > 0) {
      setCurrentPropertyIndex(prev => prev - 1);
    }
  }, [currentPropertyIndex]);

  useEffect(() => {
    if (!isOpen || !currentProperty) {
      setFeatureDetector(null);
      return;
  }
    
  // Initialize feature detector on modal open
  const imageElement = imageContainerRef.current?.querySelector('img, video');
    if (imageElement) {
      const detector = new FeatureDetector(imageElement);
      setFeatureDetector(detector);
    }
  }, [isOpen, currentProperty, currentPropertyIndex]);

  useEffect(() => {
    if (!featureDetector || !imageContainerRef.current) return;
    
    const handleMouseMove = (e) => {
      featureDetector.trackCursorMovement(e);
    };
    
    const imageElement = imageContainerRef.current.querySelector('img, video');
    if (imageElement) {
      imageElement.addEventListener('mousemove', handleMouseMove);
      return () => imageElement.removeEventListener('mousemove', handleMouseMove);
    }
  }, [featureDetector]);

    // Track interaction when modal closes or property changes
  useEffect(() => {
    return () => {
      // When modal closes or property changes, submit any pending tracking data
      if (featureDetector && currentProperty) {
        featureDetector.submitToBackend(currentProperty.id, api).catch(err => {
          console.error('Failed to submit feature tracking on cleanup:', err);
        });
      }
    };
  }, [featureDetector, currentProperty?.id, currentProperty]);


  // Keyboard support
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowUp') goToPrevProperty();
      if (e.key === 'ArrowDown') goToNextProperty();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, prevImage, nextImage, goToPrevProperty, goToNextProperty]);

  
  useEffect(() => {
  console.log('Received properties count:', properties.length);
  console.log('Current index:', currentPropertyIndex);
}, [properties, currentPropertyIndex]);

useEffect(() => {
  if (!isOpen) return;

  const handleWheel = (e) => {
    if (e.deltaY > 0) {
      goToNextProperty();
    } else if (e.deltaY < 0) {
      goToPrevProperty();
    }
  };

  const imageContainer = document.querySelector('.property-modal-image-container');
  if (imageContainer) {
    imageContainer.addEventListener('wheel', handleWheel, { passive: false });
    return () => imageContainer.removeEventListener('wheel', handleWheel);
  }
}, [isOpen, goToNextProperty, goToPrevProperty]);


const handleUnifiedTouchStart = (e) => {
  const touch = e.touches[0];
  setStartX(touch.clientX);
  setStartY(touch.clientY);
  setEndX(null);
  setEndY(null);
};

const handleUnifiedTouchMove = (e) => {
  if (!startX || !startY) return;
  const touch = e.touches[0];
  setEndX(touch.clientX);
  setEndY(touch.clientY);
};

const handleUnifiedTouchEnd = () => {
  if (!startX || !startY || !endX || !endY) return;

  const deltaX = endX - startX;
  const deltaY = endY - startY;
  const absDeltaX = Math.abs(deltaX);
  const absDeltaY = Math.abs(deltaY);

  // Determine primary direction
  if (absDeltaY > absDeltaX) {
    // Vertical swipe → navigate between properties
    if (absDeltaY > MIN_VERTICAL_SWIPE) {
      if (deltaY > 0) {
        // Swipe down → previous property
        goToPrevProperty();
      } else {
        // Swipe up → next property
        goToNextProperty();
      }
    }
  } else {
    // Horizontal swipe → navigate images
    if (absDeltaX > MIN_HORIZONTAL_SWIPE) {
      if (deltaX > 0) {
        // Swipe right → previous image
        prevImage();
      } else {
        // Swipe left → next image
        nextImage();
      }
    }
  }

  // Reset
  setStartX(null);
  setStartY(null);
  setEndX(null);
  setEndY(null);
};


// Mouse drag handlers (for desktop/laptop)
const handleMouseDown = (e) => {
  // Skip if clicked on interactive elements (buttons, etc.)
  if (e.target.closest('button, a, input, textarea')) return;
  setDragStartX(e.clientX);
  setDragStartY(e.clientY);
  setDragEndX(null);
  setDragEndY(null);
  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
};

const handleMouseMove = (e) => {
  if (dragStartX === null || dragStartY === null) return;
  setDragEndX(e.clientX);
  setDragEndY(e.clientY);
};

const handleMouseUp = () => {
  if (dragStartX === null || dragStartY === null || dragEndX === null || dragEndY === null) {
    cleanupMouseListeners();
    return;
  }

  const deltaX = dragEndX - dragStartX;
  const deltaY = dragEndY - dragStartY;
  const absDeltaX = Math.abs(deltaX);
  const absDeltaY = Math.abs(deltaY);

  if (absDeltaY > absDeltaX) {
    // Vertical drag → property navigation
    if (absDeltaY > MIN_VERTICAL_SWIPE) {
      if (deltaY > 0) {
        goToPrevProperty(); // drag down → previous
      } else {
        goToNextProperty(); // drag up → next
      }
    }
  } else {
    // Horizontal drag → image navigation
    if (absDeltaX > MIN_HORIZONTAL_SWIPE) {
      if (deltaX > 0) {
        prevImage();
      } else {
        nextImage(); 
      }
    }
  }

  cleanupMouseListeners();
};

const cleanupMouseListeners = () => {
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mouseup', handleMouseUp);
  setDragStartX(null);
  setDragStartY(null);
  setDragEndX(null);
  setDragEndY(null);
};

  // Actions
    const handleMessageClick = async () => {
    if (!currentProperty) return;

    try {
      const res = await api.post('/conversations/get_or_create_with_property/', {
        property_id: currentProperty.id,
      });

      const conversation = res.data;
      onClose();
      onMessageOwner?.(currentProperty, conversation);
    } catch (err) {
      console.error('Conversation creation failed:', err);
    }
  };


  const toggleLike = async () => {
    if (!currentProperty?.id) return;

    try {
      // Track the like interaction
      if (featureDetector) {
        featureDetector.trackLike();
        await featureDetector.submitToBackend(currentProperty.id, api);
      }

      const csrfToken = await getCSRFToken();

      const res = await api.post(
        `/properties/${currentProperty.id}/like/`,
        {},
        {
          headers: csrfToken ? { 'X-CSRFToken': csrfToken } : {},
        }
      );

      // Use backend status to decide state
      const status = res.data?.status;
      if (status === 'liked') {
        setLiked(true);
        setLikesCount(res.data.likes_count ?? (prev => prev + 1));
      } else if (status === 'unliked') {
        setLiked(false);
        setLikesCount(res.data.likes_count ?? (prev => Math.max(prev - 1, 0)));
      } else {
        // Fallback to optimistic if shape unexpected
        const newLiked = !liked;
        setLiked(newLiked);
        setLikesCount(prev => newLiked ? prev + 1 : Math.max(prev - 1, 0));
      }
    } catch (err) {
      console.error('Like failed:', err);
      alert(t('propertyModal.errors.likeFailed'));
    }
  };


  const openComments = async () => {
    if (!currentProperty) return;
    try {
      const res = await api.get(`/properties/${currentProperty.id}/comments/`);
      setCommentPanel({
        isOpen: true,
        comments: res.data || []
      });
    } catch (err) {
      console.warn('Failed to load comments');
      setCommentPanel({
        isOpen: true,
        comments: []
      });
    }
  };

  const handleShare = () => {
    if (navigator.share && currentProperty) {
      navigator.share({
        title: currentProperty.title,
        text: currentProperty.description,
        url: window.location.href
      }).catch(console.warn);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  if (!isOpen || !currentProperty) return null;

  const hasMultipleImages = currentProperty.images && currentProperty.images.length > 1;

  return (
    <div 
        className="fixed inset-0 z-[100] bg-[var(--bg-black)]"
        onTouchStart={handleUnifiedTouchStart}
        onTouchMove={handleUnifiedTouchMove}
        onTouchEnd={handleUnifiedTouchEnd}
        onMouseDown={handleMouseDown}
      >
     <div className="h-screen w-full flex flex-col md:flex-row overflow-y-auto md:overflow-hidden">
      {/* LEFT SIDE - Image Gallery */}
      <div className="relative flex-1 bg-[var(--bg-black)] flex items-center justify-center">          
        <button
          onClick={onClose}
          className="absolute top-4 left-4 p-2 bg-transparent border-none backdrop-blur-md text-pink-500 rounded-full transition-all z-10 image-nav-button"
        >
          <X size={20} className="font-bold" />
        </button>

        {/* Image — disable touch events here so they don't interfere */}
        <div 
          className="property-modal-image-container relative w-full h-full flex items-center justify-center"
        >
          {currentProperty.images && currentProperty.images.length > 0 && (
            <img
              src={currentProperty.images[currentImageIndex]?.image || '/fallback.jpg'}
              alt={currentProperty.title}
              className="max-w-full max-h-full object-contain rounded-lg"
              draggable={false}
            />

          )}

            {/* Arrows */}
            {hasMultipleImages && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 border border-solid border-white/40 bg-[var(--bg-black)] backdrop-blur-md text-[var(--text-black)] rounded-full hover:bg-black/20 transition-all z-10 image-nav-button"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 border border-solid border-white/40 bg-[var(--bg-black)] backdrop-blur-md text-[var(--text-black)] rounded-full hover:bg-black/80 transition-all z-10 image-nav-button"
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}

            {/* Dots */}
            {hasMultipleImages && (
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
                {currentProperty.images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`transition-all duration-300 rounded-full ${
                      index === currentImageIndex
                        ? 'w-8 h-2 bg-pink-700 border-none'
                        : 'w-2 h-2 bg-pink-700 border-none'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT SIDE - Property Details */}
        <div className="w-full md:w-96 lg:w-[420px] p-2 bg-[var(--bg-black)] border-none flex flex-col md:max-h-full">
          {/* Owner Header */}
          <div className="flex items-center justify-between p-2 bg-[var(--bg-black)] border-none">
           <div className="flex items-center gap-3 flex-1 min-w-0 p-4 ">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (currentProperty.owner?.id) {
                  navigate(`/user/${currentProperty.owner.id}`);
                }
              }}
              className="flex items-center gap-3 bg-transparent focus:outline-none group border-none"
            >
              {(() => {
                const displayName = currentProperty.owner?.username || 'U';
                const initials = displayName.charAt(0).toUpperCase();
                const bgColor = stringToColor(currentProperty.owner?.username || currentProperty.owner?.id || 'user').substring(1);
                const avatarUrl = currentProperty.owner?.avatar || 
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=${bgColor}&color=fff&size=150`;
                return (
                  <img
                    src={avatarUrl}
                    alt={currentProperty.owner?.username || 'User'}
                    className="w-12 h-12 rounded-full ring-2 ring-pink-500 flex-shrink-0 group-hover:ring-pink-300 transition-all"
                  />
                );
              })()}
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-[var(--text-black)] truncate group-hover:text-pink-400 transition-colors">
                    {currentProperty.owner?.username || t('propertyModal.owner.fallback')}
                  </h3>
                  {currentProperty.owner?.verified && (
                    <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                    </svg>
                  )}
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {currentProperty.posted_at || t('propertyModal.owner.posted')}
                </p>
              </div>
            </button>
          </div>

            <button
              onClick={toggleFollow}
              className={`px-4 py-1.5 rounded-full text-sm font-bold transition-all ${
                isFollowing
                  ? 'bg-gray-500 text-white hover:bg-gray-600 border-none'
                  : 'bg-pink-500 text-white hover:bg-pink-600 border-none'
              }`}
            >
              {isFollowing
                ? t('propertyModal.actions.following')
                : t('propertyModal.actions.follow')}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{WebkitOverflowScrolling: 'touch'}}>
            <div>
              <h2 className="text-xl font-bold text-[var(--text-black)] mb-2">
                {currentProperty.title}
              </h2>
              <p className="text-[var(--text-black)] text-sm leading-relaxed">
                {currentProperty.description}
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                <MapPin size={16} className="text-pink-500" />
                <span>{currentProperty.location}</span>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${
                currentProperty.type === 'rental' ? 'bg-blue-500' :
                currentProperty.type === 'property' ? 'bg-green-500' : 'bg-purple-500'
              }`}>
                {currentProperty.type === 'rental'
                  ? t('propertyModal.property.types.rental')
                  : currentProperty.type === 'property'
                  ? t('propertyModal.property.types.property')
                  : t('propertyModal.property.types.hotel')}
              </span>
            </div>

            <div className="bg-[var(--bg-black)] p-4 rounded-xl border border-[var(--border)] border-solid">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-black text-pink-500">
                  {formatPrice(currentProperty.price_rwf)}
                </span>
                <span className="text-lg font-bold text-[var(--text-black)]">{userCurrency}</span>
                {currentProperty.type === 'rental' && (
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('propertyModal.property.price.monthly')}</span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="border border-[var(--border)] border-solid p-2 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <BedDouble size={16} className="text-pink-500" />
                  <span className="text-xs text-[var(--text-black)]">
                    {t('propertyModal.property.details.bedrooms')}
                  </span>
                </div>
                <p className="text-xl font-bold text-[var(--text-black)]">{currentProperty.bedrooms}</p>
              </div>
              
              <div className="p-2 rounded-lg border border-[var(--border)] border-solid">
                <div className="flex items-center gap-2 mb-1">
                  <Bath size={16} className="text-blue-500" />
                  <span className="text-xs text-[var(--text-black)]">
                    {t('propertyModal.property.details.bathrooms')}
                  </span>
                </div>
                <p className="text-xl font-bold text-[var(--text-black)]">{currentProperty.bathrooms}</p>
              </div>
              
              <div className="p-2 rounded-lg border border-[var(--border)] border-solid">
                <div className="flex items-center gap-2 mb-1">
                  <Home size={16} className="text-green-500" />
                  <span className="text-xs text-[var(--text-black)]">
                    {t('propertyModal.property.details.city')}
                  </span>
                </div>
                <p className="text-sm font-bold text-[var(--text-black)] truncate">{currentProperty.city}</p>
              </div>

              <div className="p-2 rounded-lg border border-[var(--border)] border-solid">
                <div className="flex items-center gap-2 mb-1">
                  <Eye size={16} className="text-purple-500" />
                  <span className="text-xs text-[var(--text-black)]">
                    {t('propertyModal.property.details.views')}
                  </span>
                </div>
                <p className="text-xl font-bold text-[var(--text-black)]">
                  {formatNumber(currentProperty.views_count || 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-[var(--border)] p-2 bg-[var(--bg-black)]">
            <div className="flex items-center justify-around mb-4">
              <button onClick={toggleLike} className="flex flex-col items-center bg-[var(--bg-black)] border border-none gap-1 hover:scale-110 transition-transform">
                <div className="p-2 rounded-full border border-[var(--border)] border-solid">
                  <Heart size={24} className={`transition-all ${liked ? 'fill-pink-500 text-pink-500' : 'text-[var(--text-black)]'}`} />
                </div>
                <span className="text-xs font-bold text-[var(--text-black)]">{formatNumber(likesCount)}</span>
              </button>

              <button onClick={openComments} className="flex flex-col items-center bg-[var(--bg-black)] border border-none gap-1 hover:scale-110 transition-transform">
                <div className="p-2 rounded-full border border-[var(--border)] border-solid">
                  <MessageCircle size={24} className="text-[var(--text-black)]" />
                </div>
                <span className="text-xs font-bold text-[var(--text-black)]">{formatNumber(commentsCount)}</span>
              </button>

              <button onClick={handleShare} className="flex flex-col items-center bg-[var(--bg-black)] border border-none gap-1 hover:scale-110 transition-transform">
                <div className="p-2 rounded-full border border-[var(--border)] border-solid">
                  <Share2 size={24} className="text-[var(--text-black)]" />
                </div>
                <span className="text-xs font-bold text-[var(--text-black)]">{t('propertyModal.actions.share')}</span>
              </button>
            </div>

            <button
              onClick={handleMessageClick}
              className="w-full border border-[var(--border)] border-solid flex items-center justify-center text-[var(--text-black)] gap-2 px-6 py-3 bg-[var(--bg-black)] to-purple-500 rounded-full font-bold hover:from-pink-600 hover:to-purple-600 transition-all shadow-lg"
            >
              <MessageCircle size={20} />
              <span>{t('propertyModal.actions.messageOwner')}</span>
            </button>

            {currentProperty.contact_phone && (
              <button
                onClick={() => window.open(`tel:${currentProperty.contact_phone}`, '_self')}
                className="mt-2 flex items-center gap-1 text-sm text-pink-500 hover:underline"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.552 21 3 14.448 3 6.5V5z" />
                </svg>
                {t('property.callOwner')}
              </button>
            )}


            <CommentPanel
              isOpen={commentPanel.isOpen}
              onClose={() => setCommentPanel({ isOpen: false, comments: [] })}
              propertyId={currentProperty?.id}
              comments={commentPanel.comments}
              onCommentPosted={(newComment) => {
                setCommentsCount(prev => prev + 1);
                setCommentPanel(prev => ({
                  ...prev,
                  comments: [newComment, ...prev.comments]
                }));
              }}
            />

          <RelatedProperties 
            propertyId={currentProperty.id}
            onPropertyClick={(id) => {
              const idx = properties.findIndex(p => p.id === id);
              if (idx !== -1) {
                setCurrentPropertyIndex(idx);
                setCurrentImageIndex(0);
              }
            }}
            fxRates={fxRates}
            userCurrency={userCurrency}
            limit={6}
          />

          </div>
        </div>
      </div>
    </div>
  );
}